/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import '../../components/skeletonRow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSync,
  faCalendarAlt,
  faUserTie,
  faCopy,
  faCheck,
  faShoppingBag,
} from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { API_URL } from '../../config';
import { showToast } from '../../components/toast';
import DistributorReportStats from './DistributorReportStats';
import DistributorLedgerTable from './DistributorLedgerTable';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const defaultDateRange = {
  startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
};

const formatShortRupee = (value) => {
  if (value === 0 || value === '0') return '₹0';
  const val = Number(value);
  if (isNaN(val)) return '₹0';
  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (absVal >= 10000000) {
    const cr = val / 10000000;
    return `${sign}₹${cr % 1 === 0 ? cr : cr.toFixed(2).replace(/\.00$/, '')} Cr`;
  } else if (absVal >= 100000) {
    const lk = val / 100000;
    return `${sign}₹${lk % 1 === 0 ? lk : lk.toFixed(1).replace(/\.0$/, '')} L`;
  } else if (absVal >= 1000) {
    const k = val / 1000;
    return `${sign}₹${k % 1 === 0 ? k : k.toFixed(1).replace(/\.0$/, '')} K`;
  }
  return `₹${val.toLocaleString('en-IN')}`;
};

const DistributorReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Date Range Filter State
  const [dateRange, setDateRange] = useState(defaultDateRange);

  // Distributor Profile & Analytics
  const [distributorData, setDistributorData] = useState({
    distributor: {},
    kpi: {},
    charts: { trends: [], statusBreakdown: [], servicesBreakdown: [] },
  });

  // Detailed Ledger Table State
  const [ledgerTableData, setLedgerTableData] = useState([]);
  const [ledgerTableTotal, setLedgerTableTotal] = useState(0);
  const [ledgerTablePage, setLedgerTablePage] = useState(1);
  const [ledgerTableOrderBy, setLedgerTableOrderBy] = useState('id');
  const [ledgerTableOrderDir, setLedgerTableOrderDir] = useState('DESC');
  const [ledgerColumnFilters, setLedgerColumnFilters] = useState({
    orderId: '',
    distributor: '',
    subscriber: '',
    service: '',
    status: '',
  });

  // Fetch Distributor Profile, KPIs, and Charts
  const fetchDistributorDetails = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
      };

      const res = await axios.post(`${API_URL}report-distributor/${id}/details`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        setDistributorData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch distributor details', err);
      showToast.error('Failed to load distributor details.');
    } finally {
      setLoading(false);
    }
  }, [id, dateRange]);

  // Fetch Detailed Ledger for this distributor
  const fetchLedgerTable = useCallback(
    async (
      page = 1,
      orderBy = ledgerTableOrderBy,
      orderDir = ledgerTableOrderDir,
      colFilters = ledgerColumnFilters
    ) => {
      if (!id) return;
      try {
        const token = localStorage.getItem('authToken');
        const searchTerms = [
          colFilters.orderId,
          colFilters.subscriber,
          colFilters.service,
        ]
          .filter(Boolean)
          .join(' ');

        const payload = {
          distributorId: Number(id),
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
          status: colFilters.status && colFilters.status !== 'All' ? colFilters.status : undefined,
          search: searchTerms || undefined,
          page,
          limit: 20,
          orderBy,
          orderDir,
        };

        const res = await axios.post(`${API_URL}report-distributor/ledger`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.success) {
          setLedgerTableData(res.data.data.list || []);
          setLedgerTableTotal(res.data.data.total || 0);
          setLedgerTablePage(page);
        }
      } catch (err) {
        console.error('Failed to fetch distributor ledger table', err);
      }
    },
    [id, dateRange, ledgerTableOrderBy, ledgerTableOrderDir, ledgerColumnFilters]
  );

  useEffect(() => {
    fetchDistributorDetails();
    fetchLedgerTable(1);
  }, [fetchDistributorDetails, fetchLedgerTable]);

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setDateRange(defaultDateRange);
    setLedgerColumnFilters({ orderId: '', distributor: '', subscriber: '', service: '', status: '' });
    fetchLedgerTable(1, 'id', 'DESC', { orderId: '', distributor: '', subscriber: '', service: '', status: '' });
  };

  const hasAnyFilter = () => {
    return (
      ledgerColumnFilters.orderId ||
      ledgerColumnFilters.subscriber ||
      ledgerColumnFilters.service ||
      ledgerColumnFilters.status
    );
  };

  const handleCopyCode = () => {
    const code = distributorData.distributor?.referralCode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      showToast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Export Single Distributor Report to CSV
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        distributorId: Number(id),
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        status: ledgerColumnFilters.status ? ledgerColumnFilters.status : undefined,
      };

      const res = await axios.post(`${API_URL}report-distributor/export`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const distCode = distributorData.distributor?.referralCode || id;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `distributor_${distCode}_report_${moment().format('YYYYMMDD_HHmm')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Distributor report exported successfully!');
    } catch (err) {
      console.error('Export error', err);
      showToast.error('Failed to export report.');
    } finally {
      setExportLoading(false);
    }
  };

  // Pagination Details
  const totalPages = Math.max(1, Math.ceil(ledgerTableTotal / 20));
  const startItem = ledgerTableTotal === 0 ? 0 : (ledgerTablePage - 1) * 20 + 1;
  const endItem = Math.min(ledgerTablePage * 20, ledgerTableTotal);
  const showingRecords = `${startItem} to ${endItem} of ${ledgerTableTotal}`;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchLedgerTable(newPage);
    }
  };

  const refreshData = () => {
    fetchDistributorDetails();
    fetchLedgerTable(ledgerTablePage);
  };

  const dist = distributorData.distributor || {};
  const charts = distributorData.charts || {};
  const { trends = [], statusBreakdown = [], servicesBreakdown = [] } = charts;

  // Chart Data: Trends
  const trendLabels = trends.map((t) => t.label || t.month);
  const trendBusinessData = trends.map((t) => t.business || 0);
  const trendCommissionData = trends.map((t) => t.commission || 0);

  const trendChartData = {
    labels: trendLabels.length > 0 ? trendLabels : ['No Data'],
    datasets: [
      {
        type: 'bar',
        label: 'Gross Sales (₹)',
        data: trendBusinessData.length > 0 ? trendBusinessData : [0],
        backgroundColor: '#348fe2',
        borderRadius: 4,
        maxBarThickness: 38,
      },
      {
        type: 'line',
        label: 'Commission (₹)',
        data: trendCommissionData.length > 0 ? trendCommissionData : [0],
        borderColor: '#00acac',
        backgroundColor: 'rgba(0, 172, 172, 0.15)',
        tension: 0.3,
        fill: false,
        pointRadius: 5,
        pointBackgroundColor: '#00acac',
        pointHoverRadius: 7,
      },
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ₹${Number(context.parsed.y).toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        grid: { color: '#f1f5f9' },
        ticks: { callback: (value) => formatShortRupee(value) },
      },
    },
  };

  // Chart Data: Status Breakdown
  const statusLabels = statusBreakdown.map((s) => s.status || 'Unknown');
  const statusAmounts = statusBreakdown.map((s) => s.amount || 0);
  const statusColors = ['#00acac', '#348fe2', '#f59c1a', '#ff5b57', '#8753de'];

  const statusDoughnutData = {
    labels: statusLabels.length > 0 ? statusLabels : ['No Data'],
    datasets: [
      {
        data: statusAmounts.length > 0 ? statusAmounts : [1],
        backgroundColor: statusAmounts.length > 0 ? statusColors.slice(0, statusLabels.length) : ['#e2e8f0'],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const statusDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.label}: ₹${Number(context.parsed).toLocaleString('en-IN')}`,
        },
      },
    },
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content">
        {/* Top Header: Back Button + Breadcrumbs on Left, Date Filter on Right */}
        <div className="row pb-3 align-items-center">
          <div className="col-md-6 d-flex align-items-center gap-3">
            <button
              className="btn btn-default btn-sm"
              onClick={() => navigate('/Report/Distributor')}
              title="Back to Distributor Report"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-1" /> Back to Report
            </button>
            <ul className="breadcrumb" style={{ margin: 0, padding: 0, background: 'transparent' }}>
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ textDecoration: 'none' }}>
                  <FontAwesomeIcon icon={faHome} /> Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/Report/Distributor" style={{ textDecoration: 'none' }}>
                  Distributor Report
                </a>
              </li>
              <li className="breadcrumb-item active">{dist.name || `Distributor #${id}`}</li>
            </ul>
          </div>

          {/* Top Right Date Filter */}
          <div className="col-md-6 d-flex justify-content-end align-items-center gap-2">
            <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #ced4da', borderRadius: '4px', padding: '3px 8px', gap: '6px' }}>
              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6c757d', fontSize: '12px' }} />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>From:</span>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>To:</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
            </div>

            <button
              className="btn btn-default btn-sm"
              onClick={refreshData}
              title="Apply Date Filter & Refresh"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>
          </div>
        </div>

        {/* Distributor Profile Card Strip */}
        <div className="panel panel-default with-rounded-corner with-shadow mb-4">
          <div className="panel-body p-3" style={{ background: '#f8fafc', borderRadius: '6px' }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '50%',
                    background: '#e0f2fe',
                    color: '#0284c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                  }}
                >
                  <FontAwesomeIcon icon={faUserTie} />
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>
                    {dist.name}
                    {dist.username && <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '400', marginLeft: '6px' }}>({dist.username})</span>}
                  </h4>
                  <div className="d-flex align-items-center gap-3 flex-wrap" style={{ fontSize: '12.5px', color: '#475569' }}>
                    <span><strong>Mobile:</strong> {dist.mobile || '—'}</span>
                    <span>•</span>
                    <span><strong>Email:</strong> {dist.email || '—'}</span>
                    <span>•</span>
                    <span><strong>Commission Rate:</strong> <span style={{ color: '#0284c7', fontWeight: '600' }}>{dist.commissionPercentage}%</span></span>
                    <span>•</span>
                    <span><strong>Joined:</strong> {dist.createdOn || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Referral Code Badge with Copy */}
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Referral Code:</span>
                <div
                  style={{
                    background: '#ffffff',
                    border: '1.5px dashed #0284c7',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <code style={{ fontSize: '14px', fontWeight: '700', color: '#0284c7' }}>{dist.referralCode}</code>
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '0 2px' }}
                    title="Copy Referral Code"
                  >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} style={{ color: copied ? '#16a34a' : '#64748b' }} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Metric Widgets for this Distributor */}
        <DistributorReportStats kpi={distributorData.kpi} loading={loading} isDetailPage={true} />

        {/* Row 1: Full-Width Monthly Revenue & Commission Trend for this distributor */}
        <div className="row mb-4">
          <div className="col-lg-12 mb-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title">Monthly Revenue & Commission Trend</h4>
              </div>
              <div className="panel-body p-4">
                <div style={{ height: '280px', position: 'relative' }}>
                  <Bar data={trendChartData} options={trendChartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Commission Status Breakdown & Top Services Subscribed by this distributor's referrals */}
        <div className="row mb-4">
          {/* Commission Status Breakdown */}
          <div className="col-lg-6 mb-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title">Commission Status Breakdown</h4>
              </div>
              <div className="panel-body p-4">
                <div style={{ height: '260px', position: 'relative' }}>
                  <Doughnut data={statusDoughnutData} options={statusDoughnutOptions} />
                </div>
              </div>
            </div>
          </div>

          {/* Top Services Subscribed */}
          <div className="col-lg-6 mb-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title">Top Services Subscribed by Referred Clients</h4>
              </div>
              <div className="panel-body p-4" style={{ height: '260px', overflowY: 'auto' }}>
                {servicesBreakdown.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <FontAwesomeIcon icon={faShoppingBag} size="2x" className="mb-2 opacity-50" />
                    <p className="mb-0" style={{ fontSize: '13px' }}>No subscription orders recorded yet.</p>
                  </div>
                ) : (
                  servicesBreakdown.map((srv, idx) => {
                    const maxVol = servicesBreakdown[0]?.volume || 1;
                    const pct = Math.min(100, Math.round((srv.volume / maxVol) * 100));
                    return (
                      <div key={idx} style={{ marginBottom: '14px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>
                            {srv.serviceName}
                          </span>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            <strong>{srv.count}</strong> subs • <strong style={{ color: '#0f172a' }}>₹{srv.volume.toLocaleString('en-IN')}</strong>
                          </span>
                        </div>
                        <div className="progress" style={{ height: '6px', backgroundColor: '#f1f5f9', borderRadius: '4px' }}>
                          <div
                            className="progress-bar bg-primary"
                            role="progressbar"
                            style={{ width: `${pct}%`, borderRadius: '4px' }}
                            aria-valuenow={pct}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                        <div className="d-flex justify-content-between mt-1" style={{ fontSize: '11px', color: '#64748b' }}>
                          <span>Commission Earned: ₹{srv.commission.toLocaleString('en-IN')}</span>
                          <span>{pct}% share</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Commission & Client Transaction Ledger for this Distributor */}
        <div className="panel-heading d-flex justify-content-between align-items-center" style={{ padding: '8px 0', borderBottom: 'none' }}>
          <div className="d-flex align-items-center gap-2">
            <h4 className="panel-title" style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
              Client Subscriptions & Commission Ledger ({dist.name})
            </h4>

            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs"
                style={{ marginLeft: '10px' }}
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>

          <div className="d-flex align-items-center">
            <span style={{ fontSize: '0.8rem' }} className="me-3">
              {loading ? (
                <span className="skeleton-text" style={{ width: '140px', display: 'inline-block' }}></span>
              ) : (
                showingRecords
              )}
            </span>

            <button
              className="btn btn-default btn-sm mx-1"
              onClick={refreshData}
              title="Refresh Data"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>

            <button
              className="btn btn-default btn-sm mx-1"
              onClick={handleExportCSV}
              disabled={exportLoading || ledgerTableTotal === 0}
              title="Export Report"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} /> Export Ledger
                </>
              )}
            </button>

            <button
              className={`btn btn-default btn-sm ${ledgerTablePage === 1 ? 'd-none' : ''}`}
              title="Previous"
              onClick={() => handlePageChange(ledgerTablePage - 1)}
              disabled={ledgerTablePage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className={`btn btn-default btn-sm ${ledgerTablePage >= totalPages ? 'd-none' : ''}`}
              title="Next"
              onClick={() => handlePageChange(ledgerTablePage + 1)}
              disabled={ledgerTablePage >= totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        <DistributorLedgerTable
          data={ledgerTableData}
          page={ledgerTablePage}
          limit={20}
          loading={loading}
          orderBy={ledgerTableOrderBy}
          orderDir={ledgerTableOrderDir}
          columnFilters={ledgerColumnFilters}
          onColumnFilterChange={(col, val) =>
            setLedgerColumnFilters((prev) => ({ ...prev, [col]: val }))
          }
          onApplyFilters={() => fetchLedgerTable(1, ledgerTableOrderBy, ledgerTableOrderDir, ledgerColumnFilters)}
          onSortChange={(col, dir) => {
            setLedgerTableOrderBy(col);
            setLedgerTableOrderDir(dir);
            fetchLedgerTable(1, col, dir, ledgerColumnFilters);
          }}
        />
      </div>
    </div>
  );
};

export default DistributorReportDetail;
